import { css } from "@emotion/react";
import styled from "@emotion/styled";
import StepIndicator from "../../../components/expertMode/StepIndicator";
import Table from "../../../components/expertMode/Table";
import { useAppSelector } from "../../../hooks";
import { ExpertModeTheme } from "../../../theme/expert-mode";
import imageDownloadCloud from "../../../../public/img/downloadCloud.svg"
import imageUploadCloud from "../../../../public/img/uploadCloud.svg"
import imagePing from "../../../../public/img/ping.svg"
import { formatSpeed } from "../../../helper/formatSpeeds";
import { formatPingTime } from "../../../helper/unitsFormatting";
import { FC, useEffect, useState } from "react";
import Halogen from "../../../components/Halogen";
import * as PursComponents from "@fusion/fusion/dist/output/Visonum.TsExport"

// @refresh reset

const Root = styled.div(
    css`
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding-right: 40%;
    `)

const PlaceholderImg = styled.img(
    css`
        width: 100%;
    `)

const Section = styled.div(css``)

const Headline = styled.div(
    css`
        font-family: inter-regular;
        font-style: normal;
        font-weight: 500;
        font-size: 20px;
        line-height: 24px;
        margin-bottom: 16px;
    `)

const ChartBox = styled.div<{ expertTheme: ExpertModeTheme }>(
    props => css`
        border-radius: 8px;
        border: 1px solid ${props.expertTheme.colors.subtle};
        padding: 10px;
    `)


const ChartHeading = styled.div<{ expertTheme: ExpertModeTheme }>(
    props => css`
        font-family: inter-regular;
        font-style: normal;
        font-weight: 700;
        font-size: 24px;
        line-height: 20px;
        margin-bottom: 10px;
    `)

const ChartHeadingUnit = styled.span<{ expertTheme: ExpertModeTheme }>(
    props => css`
        font-family: inter-regular;
        font-style: normal;
        font-weight: 700;
        font-size: 12px;
        margin-left: 5px;
    `)


type Props = {
    expertTheme: ExpertModeTheme
}

const RealtimeTab: FC<Props> = (props) => {
    const state = useAppSelector(state => state.measurement);
    
    // only needed for mocking
    const [mockStartTimeSec, setMockStartTimeSec] = useState<null | number>(null) 


    useEffect(() => {
        const mockStartTimeMs = Date.now()
        setMockStartTimeSec( mockStartTimeMs / 1000)
    }, [])

    if (!mockStartTimeSec) return <></>

    const getStateInfo = () => {
        switch (state.kind) {
            case "Downloading":
                return { text: "Download wird gemessen...", icon: imageDownloadCloud.src }

            case "Uploading":
                return { text: "Upload wird gemessen...", icon: imageUploadCloud.src }

            case "Pinging":
                return { text: "Ping wird gemessen...", icon: imagePing.src }

            default:
                return undefined

        }
    }

    return (
        <Root>
            <StepIndicator<"1" | "2" | "3"> {...props}
                currentStep={"1"}
                steps={[
                    {
                        id: "1",
                        label: "1. Stufe",
                        fromLabel: "Server",
                        toLabel: "Endgerät",
                    },
                    {
                        id: "2",
                        label: "2. Stufe",
                        fromLabel: "Server",
                        toLabel: "Router"
                    },
                    {
                        id: "3",
                        label: "3. Stufe",
                        fromLabel: "Router",
                        toLabel: "Endgerät"
                    }
                ]}
                subState={getStateInfo()}
            />
            {
                ("downloadIntermediates" in state && state.downloadIntermediates) && (() => {


                    const nowSec = Date.now() / 1000
                    const mockIntervalSec = 1

                    const downloadIntermediates = state.downloadIntermediates.map((speed, index) => ({
                        speed,
                        timestamp: { seconds: mockStartTimeSec + mockIntervalSec * (index + 1), nanos: 0 }
                    }))

                    const maxSpeed = Math.max(...downloadIntermediates.map(({ speed }) => speed))
                    const minSpeed = Math.min(...downloadIntermediates.map(({ speed }) => speed))

                    const maxSpeedEstimated = 500000000.0
                    const minSpeedEstimated = 0.0

                    const maxTimeSecEstimated = mockStartTimeSec + (mockIntervalSec * 20)
                    const minTimeSecEstimated = mockStartTimeSec

                    const timeFrameSec = 9
                    const minTimeSec =  Math.max(nowSec - timeFrameSec,mockStartTimeSec)
                    const maxTimeSec = minTimeSec + timeFrameSec

                    return (<>
                        <Section>
                            <ChartBox expertTheme={props.expertTheme}>
                                <ChartHeading expertTheme={props.expertTheme}>
                                    430,10
                                    <ChartHeadingUnit expertTheme={props.expertTheme}>
                                        (Mbit/s)
                                    </ChartHeadingUnit>
                                </ChartHeading>
                                <Halogen<PursComponents.ChartProps>
                                    componentWithRuntime={PursComponents.chart}
                                    halogenProps={{
                                        chartData: {
                                            byteCount: 0,
                                            maxSpeed,
                                            minSpeed,
                                            maxTime: { seconds: maxTimeSec, nanos: 0 },
                                            minTime: { seconds: minTimeSec, nanos: 0 },
                                            startTime: { seconds: mockStartTimeSec, nanos: 0 },
                                            streamSeries: [],
                                            totalSpeed: downloadIntermediates,
                                            smoothing: 0.14
                                        },
                                        styles: {
                                            smoothing: 0.14,
                                            colors: {
                                                line: props.expertTheme.colors.primary,
                                                gradientTop: props.expertTheme.colors.subtle
                                            }
                                        }
                                    }} />
                            </ChartBox>

                        </Section>
                    </>)
                })()
            }
            {
                ("download" in state && state.download) && (() => {
                    const speed = formatSpeed([state.download.speed]);

                    return (<>
                        <Section>
                            <Headline>Download</Headline>
                            <Table {...props}
                                headerLabels={{
                                    speed: `Download (${speed.unit})`,
                                    bytes: "Bytes",
                                    duration: "Duration (s)",
                                    outOfOrderPackets: "Out-of-order packets (%)",
                                    duplicatePackets: "Duplicate packets (%)",
                                    tcpRTT: "TCP RTT (ms)",
                                    tcpRTV: "TCP RTV (ms)"
                                }}
                                data={[
                                    {
                                        speed: speed.values[0] || "",
                                        bytes: "TBD",
                                        duration: "TBD",
                                        outOfOrderPackets: "TBD",
                                        duplicatePackets: "TBD",
                                        tcpRTT: "TBD",
                                        tcpRTV: "TBD"
                                    }
                                ]} />
                        </Section>
                    </>)
                })()
            }
            {
                ("upload" in state && state.upload) && (() => {
                    const speed = formatSpeed([state.upload.speed]);

                    return (
                        <Section>
                            <Headline>Upload</Headline>
                            <Table {...props}
                                headerLabels={{
                                    speed: `Upload (${speed.unit})`,
                                    bytes: "Bytes",
                                    duration: "Duration (s)",
                                    outOfOrderPackets: "Out-of-order packets (%)",
                                    duplicatePackets: "Duplicate packets (%)",
                                    tcpRTV: "TCP RTV (ms)",
                                    empty: ""
                                }}
                                data={[
                                    {
                                        speed: speed.values[0] || "",
                                        bytes: "TBD",
                                        duration: "TBD",
                                        outOfOrderPackets: "TBD",
                                        duplicatePackets: "TBD",
                                        tcpRTV: "TBD",
                                        empty: ""
                                    }
                                ]} />
                        </Section>)
                })()
            }
            {
                ("ping" in state && state.ping) && (() => {
                    const ping = formatPingTime(state.ping.pingTime);

                    return (
                        <Section>
                            <Headline>Ping</Headline>
                            <Table {...props}
                                headerLabels={{
                                    speed: "Ping (ms)",
                                    bytes: "Bytes",
                                    duration: "Duration (s)",
                                    outOfOrderPackets: "Out-of-order packets (%)",
                                    duplicatePackets: "Duplicate packets (%)",
                                    tcpRTV: "TCP RTV (ms)",
                                    empty: ""
                                }}
                                data={[
                                    {
                                        speed: ping,
                                        bytes: "TBD",
                                        duration: "TBD",
                                        outOfOrderPackets: "TBD",
                                        duplicatePackets: "TBD",
                                        tcpRTV: "TBD",
                                        empty: ""
                                    }
                                ]} />
                        </Section>)
                })()
            }
        </Root>
    )
}

export default RealtimeTab